// Menu access rules driven by the logged-in user's plan and add-ons.
// The user object comes from the "user" cookie via TokenService.getUser().

// ---- Plans -------------------------------------------------------------
// Values of user.plan_details[0].feature_type
export const PLAN = {
  CORE: "0",
  CORE_AI: "1",
};

// Reusable allowlists for the `feature_type` key in menuConfig
export const ALL_PLANS = [PLAN.CORE, PLAN.CORE_AI]; // every plan
export const AI_ONLY = [PLAN.CORE_AI]; // Core + AI only
export const CORE_ONLY = [PLAN.CORE]; // Core only

// ---- Helpers -----------------------------------------------------------
// "Fee Management" -> "feemanagement", "fees" -> "fees"
const normalize = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");

// A user can hold only one plan, so the first entry is the plan.
export const getPlanFeature = (user) =>
  String(user?.plan_details?.[0]?.feature_type ?? "");

export const getUserAddons = (user) =>
  Array.isArray(user?.add_ons) ? user.add_ons : [];

// ---- Rule 1: plan ------------------------------------------------------
// Deny by default: a node with no feature_type array is hidden.
export const hasFeatureAccess = (item, user) => {
  const allowed = item?.feature_type;
  if (!Array.isArray(allowed) || allowed.length === 0) return false;
  return allowed.map(String).includes(getPlanFeature(user));
};

// ---- Rule 2: add-on ----------------------------------------------------
// `addon` absent/false -> no add-on needed.
// `addon: true`        -> match this node's id/title against the user's add_ons.
// `addon: "some_key"`  -> match that explicit key instead (escape hatch).
export const hasAddonAccess = (item, user) => {
  if (!item?.addon) return true;

  const wanted =
    typeof item.addon === "string"
      ? [normalize(item.addon)]
      : [normalize(item.id), normalize(item.title)];

  return getUserAddons(user).some((addon) => {
    const owned = [normalize(addon?.addon_key), normalize(addon?.addon_name)];
    return wanted.some((w) => w && owned.includes(w));
  });
};

// ---- Rule 3: organisation plan -----------------------------------------
// Some menus are only for organisation plans (plan_details[0].is_org === 1).
// Accepts 1 or "1"; a missing is_org counts as not an organisation.
export const isOrgPlan = (user) =>
  Number(user?.plan_details?.[0]?.is_org) === 1;

// `is_org` absent/false on a node -> no organisation requirement.
export const hasOrgAccess = (item, user) => {
  if (!item?.is_org) return true;
  return isOrgPlan(user);
};

// ---- Page-level feature checks -----------------------------------------
// Same rules as the menu, for pages that show/hide a widget instead of a node.

// Mirrors the "fees" menu node (id/title matched against user.add_ons).
export const FEE_MODULE_ADDON = { id: "fees", title: "Fees", addon: true };

export const hasFeeModuleAddon = (user) =>
  hasAddonAccess(FEE_MODULE_ADDON, user);

export const hasAiPlan = (user) => getPlanFeature(user) === PLAN.CORE_AI;

// ---- Combined ----------------------------------------------------------
export const canAccessMenu = (item, user) =>
  hasFeatureAccess(item, user) &&
  hasAddonAccess(item, user) &&
  hasOrgAccess(item, user);

// Recursive, non-mutating menu filter (any depth).
export const filterMenuItems = (items = [], user) =>
  items.reduce((acc, item) => {
    if (!canAccessMenu(item, user)) return acc;
    if (!item.children) {
      acc.push(item);
      return acc;
    }
    const children = filterMenuItems(item.children, user);
    if (children.length === 0) return acc; // parent left with no children
    acc.push({ ...item, children });
    return acc;
  }, []);
